import {
  WSElement,
  WSList,
  WSPage,
  WSPageToolbar
} from "@wingspanhq/fe-component-library";
import { PayrollWorkflowStrategy } from "@wingspanhq/payments/dist/interfaces";
import pluralize from "pluralize";
import React, { useMemo } from "react";
import {
  Redirect,
  Route,
  Switch,
  useHistory,
  useLocation
} from "react-router-dom";
import { Tabs } from "../../components/Tabs";
import { AllDeductions } from "../../Invoices/screens/payableDeductions/AllDeductions";
import { AlertPayrollDebitReturn } from "../../Invoices/screens/payables/AlertPayrollDebitReturn";
import { AlertPayrollRunInProgress } from "../../Invoices/screens/payables/AlertPayrollRunInProgress";
import { CreatePayable } from "../../Invoices/screens/payables/CreatePayable";
import { EditPayable } from "../../Invoices/screens/payables/EditPayable";
import { OffCyclePayroll } from "../../Invoices/screens/payables/OffCyclePayroll";
import { PayablesDisputed } from "../../Invoices/screens/payables/PayablesDisputed";
import { PayrollSettingsModal } from "../../Invoices/screens/payables/PayrollSettingsModal";
import { useUserId } from "../../query/hooks/helpers";
import { useQueryPayrollSettings } from "../../query/payments/queries";
import { routeHelpersImportPayablesStart } from "../BulkImporter/paths";
import { useCustomization } from "../customization";
import { PATH_SETTINGS_PAYROLL } from "../Settings/paths";
import {
  PATH_PAYABLES,
  PATH_PAYABLES_CREATE,
  PATH_PAYABLES_DEDUCTIONS,
  PATH_PAYABLES_DISPUTED,
  PATH_PAYABLES_DRAFT,
  PATH_PAYABLES_EDIT,
  PATH_PAYABLES_OFF_CYCLE_PAYROLL,
  PATH_PAYABLES_PAYROLL_RUN,
  PATH_PAYABLES_PAYROLL_RUNS,
  PayablesTabSlug,
  routeHelpersPayables,
  routeHelpersPayablesTab
} from "./paths";
import { RoutePayablesAll } from "./routes/RoutePayablesAll";
import { RoutePayablesDashboard } from "./routes/RoutePayablesDashboard";
import { RoutePayablesDrafts } from "./routes/RoutePayablesDrafts";
import { RoutePayablesPaid } from "./routes/RoutePayablesPaid";
import { RoutePayablesPayrollRun } from "./routes/RoutePayablesPayrollRun";
import { RoutePayablesPayrollRuns } from "./routes/RoutePayablesPayrollRuns";
import { RoutePayablesScheduled } from "./routes/RoutePayablesScheduled";
import { RoutePayablesToApprove } from "./routes/RoutePayablesToApprove";
import { RoutePayablesToPreApprove } from "./routes/RoutePayablesToPreApprove";

export const Payables: React.FC = () => {
  const { terminology } = useCustomization();
  const userId = useUserId();
  const history = useHistory();
  const location = useLocation();
  const payrollSettingsQuery = useQueryPayrollSettings(userId, {
    retry: false
  });

  const goToPayablesImportStart = routeHelpersImportPayablesStart.useGoTo();

  const isDualStage =
    payrollSettingsQuery.data?.workflow === PayrollWorkflowStrategy.DualStage;

  const pathForTab = (tab: PayablesTabSlug) =>
    routeHelpersPayablesTab.createLocationObject({ tab }).pathname ?? "";

  const tabs = useMemo(() => {
    const tabs: { label: string; path: string }[] = [];

    tabs.push({
      label: "Dashboard",
      path: pathForTab("dashboard")
    });

    if (isDualStage) {
      tabs.push({
        label: "To Pre-Approve",
        path: pathForTab("to-preapprove")
      });
    }

    tabs.push({
      label: "To Approve",
      path: pathForTab("to-approve")
    });

    tabs.push({
      label: "Scheduled",
      path: pathForTab("scheduled")
    });

    tabs.push({
      label: "Paid",
      path: pathForTab("paid")
    });

    tabs.push({
      label: "Payroll Runs",
      path: pathForTab("payroll-runs")
    });

    tabs.push({
      label: "All Payables",
      path: pathForTab("all")
    });

    tabs.push({
      label: "All Deductions",
      path: pathForTab("deductions")
    });

    return tabs;
  }, [isDualStage]);

  return (
    <Switch>
      <Route path={PATH_PAYABLES_CREATE} component={CreatePayable} />

      <Route path={PATH_PAYABLES_EDIT} component={EditPayable} />

      <Route path={PATH_PAYABLES_DRAFT} component={RoutePayablesDrafts} />

      <Route path={PATH_PAYABLES_DISPUTED} component={PayablesDisputed} />

      <Route
        path={PATH_PAYABLES_PAYROLL_RUN}
        component={RoutePayablesPayrollRun}
      />

      <Route path={PATH_PAYABLES}>
        <WSPage>
          <WSPageToolbar
            title={pluralize.plural(terminology().sendPaymentsPayable)}
            actions={[
              {
                buttonKind: "Primary",
                label: "Create payable",
                onClick() {
                  history.push({
                    pathname: PATH_PAYABLES_CREATE,
                    state: {
                      backPath: location.pathname
                    }
                  });
                }
              },
              {
                label: "Bulk upload payables",
                onClick: () => {
                  goToPayablesImportStart();
                }
              },
              {
                label: "Run off-cycle payroll",
                onClick: () => {
                  history.push(PATH_PAYABLES_OFF_CYCLE_PAYROLL);
                },
                hidden: !payrollSettingsQuery.data?.runsPayroll
              },
              {
                label: "Payroll settings",
                onClick: () => {
                  history.push(PATH_SETTINGS_PAYROLL);
                }
              }
            ]}
          />
          <PayrollSettingsModal />
          <WSList gap="2XL">
            <AlertPayrollDebitReturn />
            <AlertPayrollRunInProgress />

            <Tabs tabs={tabs} />

            <WSElement data-testid="payablesContainer">
              <Switch>
                <Route
                  path={pathForTab("dashboard")}
                  component={RoutePayablesDashboard}
                />

                <Route
                  path={pathForTab("to-preapprove")}
                  component={RoutePayablesToPreApprove}
                />

                <Route
                  path={pathForTab("to-approve")}
                  component={RoutePayablesToApprove}
                />

                <Route
                  path={pathForTab("scheduled")}
                  component={RoutePayablesScheduled}
                />

                <Route
                  path={pathForTab("paid")}
                  component={RoutePayablesPaid}
                />

                <Route
                  path={PATH_PAYABLES_PAYROLL_RUNS}
                  component={RoutePayablesPayrollRuns}
                />

                <Route path={pathForTab("all")} component={RoutePayablesAll} />

                <Route
                  path={PATH_PAYABLES_DEDUCTIONS}
                  component={AllDeductions}
                />

                <Route
                  path={PATH_PAYABLES_OFF_CYCLE_PAYROLL}
                  component={OffCyclePayroll}
                />

                <Redirect
                  from={routeHelpersPayables.path}
                  to={routeHelpersPayablesTab.createLocationObject({
                    tab: "dashboard"
                  })}
                />
              </Switch>
            </WSElement>
          </WSList>
        </WSPage>
      </Route>
    </Switch>
  );
};
